import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, Button, Collapse, Space } from 'antd'
import supportFaq from '../config/supportFaq'
import { openSupportChat, initializeSupportChat } from '../store/slices/supportChatSlice'
import './SupportContact.css'

const { Title, Paragraph } = Typography

const SupportContact = () => {
  const dispatch = useDispatch()
  const { sessionId } = useSelector((state) => state.supportChat)

  const faqSections = useMemo(() => {
    const groups = supportFaq.reduce((accumulator, faq) => {
      const category = faq.category || 'General'
      if (!accumulator[category]) {
        accumulator[category] = []
      }
      accumulator[category].push(faq)
      return accumulator
    }, {})

    return Object.entries(groups).map(([category, items]) => ({
      category,
      items,
    }))
  }, [])

  const handleOpenChat = () => {
    if (!sessionId) {
      dispatch(initializeSupportChat({ entryPoint: 'support_contact_page' }))
    }
    dispatch(openSupportChat())
  }

  const contactOptions = [
    {
      key: 'ai',
      title: 'Chat with Gamma',
      onClick: handleOpenChat,
    },
    {
      key: 'email',
      title: 'Email Support',
      href: 'mailto:support@quantumhusky.ai',
    },
  ]

  return (
    <div className="support-contact-page">
      <div className="support-content">
        <section className="support-faq-panel">
          <div className="support-panel-header support-panel-header--faq">
            <Title level={3} className="support-panel-header__title">
              Frequently Asked Questions
            </Title>
            <Paragraph className="support-panel-header__subtitle">
              Find answers to common product, billing, and subscription questions. Questions are grouped
              by topic so the page reads as one help section.
            </Paragraph>
          </div>

          <div className="support-faq-groups">
            {faqSections.map((section) => (
              <div key={section.category} className="support-faq-group">
                <div className="support-faq-group__label">{section.category}</div>
                <div className="support-faq-accordion">
                  <Collapse
                    accordion
                    bordered={false}
                    expandIconPosition="end"
                    items={section.items.map((faq) => ({
                      key: faq.id,
                      label: faq.question,
                      children: (
                        <div className="support-faq-answer">
                          <Paragraph>{faq.answer}</Paragraph>
                          {faq.links && faq.links.length > 0 && (
                            <Space size="small" wrap>
                              {faq.links.map((link) => (
                                <Button
                                  key={link.url}
                                  type="link"
                                  href={link.url}
                                  size="small"
                                >
                                  {link.label} →
                                </Button>
                              ))}
                            </Space>
                          )}
                        </div>
                      ),
                    }))}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="support-followup-panel">
          <Paragraph className="support-followup-line">
            <span className="support-followup-copy">Need more help?</span>
            <button
              type="button"
              className="support-inline-action"
              onClick={contactOptions[0].onClick}
            >
              {contactOptions[0].title}
            </button>
            <span className="support-followup-separator">or</span>
            <a
              href={contactOptions[1].href}
              className="support-inline-action"
            >
              {contactOptions[1].title}
            </a>
          </Paragraph>
        </section>
      </div>
    </div>
  )
}

export default SupportContact
